import React, { useState, useEffect } from "react";
import "./Nav.css";
import BookService from "./services/BookService";
import AudioService from "./services/AudioService";
import { useAuth } from "./contexts/AuthContext";
import { useLanguage, SUPPORTED_LANGUAGES } from "./contexts/LanguageContext";

// Pricing configuration
const PRICING = {
  AR: {
    currency: "ARS",
    monthly: { amount: "2999", period: "month" },
    annual: { amount: "22000", period: "year", badge: "Save 40%" },
    lifetime: { amount: "72000", period: "one time", badge: "Best Value" }
  },
  INTL: {
    currency: "USD",
    monthly: { amount: "2.99", period: "month" },
    annual: { amount: "24.99", period: "year", badge: "Save 30%" },
    lifetime: { amount: "71.99", period: "one time", badge: "Best Value" }
  }
};

function Nav({ onItemSelect, onAvatarClick, onLanguageChange }) {
  const { user, signOut, isPremium, userProfile } = useAuth();
  const { language, changeLanguage, getCurrentLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPricingMenu, setShowPricingMenu] = useState(false);
  const [showEducationMenu, setShowEducationMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null); // 'premium', 'education', 'account', 'language'
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [isArgentina, setIsArgentina] = useState(true); // Default to Argentina

  // Get pricing based on user's country
  const pricing = isArgentina ? PRICING.AR : PRICING.INTL;

  // Detect user's country on mount
  useEffect(() => {
    const detectCountry = async () => {
      // Check for URL override first (for demos)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('currency') === 'usd') {
        setIsArgentina(false);
        return;
      }

      try {
        const response = await fetch('/api/get-country');
        const data = await response.json();
        setIsArgentina(data.isArgentina);
      } catch (error) {
        console.error('Error detecting country:', error);
        // Default to Argentina on error
        setIsArgentina(true);
      }
    };
    detectCountry();
  }, []);

  useEffect(() => {
    const searchContent = async () => {
      if (searchQuery.trim().length > 2) {
        setIsLoading(true);
        try {
          const [books, audiobooks] = await Promise.all([
            BookService.searchGutenberg(searchQuery, 5),
            AudioService.searchAudiobooks(searchQuery, 5)
          ]);

          const combined = [];
          const maxLen = Math.max(books.length, audiobooks.length);

          for (let i = 0; i < maxLen; i++) {
            if (i < books.length) {
              combined.push({ ...books[i], type: 'book' });
            }
            if (i < audiobooks.length) {
              combined.push({ ...audiobooks[i], type: 'audiobook' });
            }
          }

          setSearchResults(combined.slice(0, 8));
          setShowResults(true);
        } catch (error) {
          console.error("Error searching content:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };

    const timeoutId = setTimeout(searchContent, 400);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectItem = (item) => {
    if (onItemSelect) {
      onItemSelect(item);
    }
    setSearchQuery("");
    setShowResults(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      handleSelectItem(searchResults[0]);
    }
  };

  const handleClickOutside = (e) => {
    if (!e.target.closest('.nav__search-container')) {
      setShowResults(false);
    }
    if (!e.target.closest('.nav__user')) {
      setShowUserMenu(false);
    }
    if (!e.target.closest('.nav__premium')) {
      setShowPricingMenu(false);
    }
    if (!e.target.closest('.nav__education')) {
      setShowEducationMenu(false);
    }
    if (!e.target.closest('.nav__language')) {
      setShowLanguageMenu(false);
    }
    if (!e.target.closest('.nav__hamburger') && !e.target.closest('.nav__mobile-menu')) {
      setShowMobileMenu(false);
      setMobileSubmenu(null);
    }
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setShowLanguageMenu(false);
    setMobileSubmenu(null);
    if (onLanguageChange) {
      onLanguageChange(langCode);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleCheckout = async (plan) => {
    if (!user) {
      setShowPricingMenu(false);
      onAvatarClick();
      return;
    }

    setCheckoutLoading(plan);
    try {
      // Use different endpoint based on country
      const endpoint = isArgentina
        ? "/api/mercadopago-subscription"
        : "/api/lemonsqueezy-checkout";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: plan,
          userId: user.uid,
          userEmail: user.email
        })
      });

      const { url, error } = await response.json();
      if (error) throw new Error(error);

      window.location.href = url;
    } catch (err) {
      console.error("Error creating checkout:", err);
      setCheckoutLoading(null);
    }
  };

  const getPlanDisplayName = () => {
    const planType = userProfile?.planType;
    if (planType === 'monthly') return 'Monthly';
    if (planType === 'annual') return 'Annual';
    if (planType === 'lifetime') return 'Lifetime';
    return 'Premium';
  };

  const getItemIcon = (type) => {
    if (type === 'audiobook') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
        </svg>
      );
    }
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/>
      </svg>
    );
  };

  return (
    <div className="nav">
      {/* Search Bar - HBO Max style */}
      <div className="nav__searchBar">
        <form onSubmit={handleSearchSubmit} className="nav__search-container">
          <div className="nav__search-box">
            <svg className="nav__search-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2"/>
              <path d="M15 15L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="nav__search-input"
              placeholder="Find books and audiobooks"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {isLoading && <div className="nav__search-loading"></div>}
          </div>

          {showResults && searchResults.length > 0 && (
            <div className="nav__search-results">
              {searchResults.map((result) => (
                <div
                  key={result.id}
                  className="nav__search-result-item"
                  onClick={() => handleSelectItem(result)}
                >
                  {result.coverUrl ? (
                    <img
                      src={result.coverUrl}
                      alt={result.title}
                      className="nav__search-result-poster"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="nav__search-result-placeholder"
                    style={{ display: result.coverUrl ? 'none' : 'flex' }}
                  >
                    {getItemIcon(result.type)}
                  </div>
                  <div className="nav__search-result-info">
                    <span className="nav__search-result-title">
                      {result.title}
                    </span>
                    <span className="nav__search-result-meta">
                      <span className="nav__search-result-type">
                        {result.type === 'audiobook' ? '🎧' : '📖'}
                      </span>
                      {result.authors?.[0] || 'Unknown author'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && searchQuery.length > 2 && !isLoading && (
            <div className="nav__search-results">
              <div className="nav__search-no-results">
                No results found
              </div>
            </div>
          )}
        </form>

        {/* Right side icons */}
        <div className="nav__icons">
          {/* Language selector */}
          <div className="nav__language">
            <div
              className="nav__language-icon"
              onClick={(e) => {
                e.stopPropagation();
                setShowUserMenu(false);
                setShowPricingMenu(false);
                setShowEducationMenu(false);
                setShowLanguageMenu(!showLanguageMenu);
              }}
            >
              <img src={getCurrentLanguage().flag} alt={getCurrentLanguage().name} className="nav__language-flag-img" />
            </div>

            {showLanguageMenu && (
              <div className="nav__language-dropdown">
                <div className="nav__language-header">
                  <span className="nav__language-title">Showing books in:</span>
                </div>
                <div className="nav__language-options">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      className={`nav__language-option ${language === lang.code ? 'nav__language-option--active' : ''}`}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      <img src={lang.flag} alt={lang.name} className="nav__language-option-flag" />
                      <span className="nav__language-option-name">{lang.name}</span>
                      {language === lang.code && (
                        <svg className="nav__language-option-check" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Premium icon */}
          <div className="nav__premium">
            <div
              className={`nav__premium-icon ${isPremium ? 'nav__premium-icon--active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowUserMenu(false);
                setShowEducationMenu(false);
                setShowLanguageMenu(false);
                setShowPricingMenu(!showPricingMenu);
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
              </svg>
            </div>

            {showPricingMenu && !isPremium && (
              <div className="nav__pricing-dropdown">
                <div className="nav__pricing-header">
                  <span className="nav__pricing-title">Upgrade to Premium</span>
                </div>
                <div className="nav__pricing-plans">
                  <button
                    className="nav__pricing-plan"
                    onClick={() => handleCheckout('monthly')}
                    disabled={checkoutLoading}
                  >
                    <span className="nav__pricing-plan-price">${pricing.monthly.amount} {pricing.currency}</span>
                    <span className="nav__pricing-plan-period">{pricing.monthly.period}</span>
                    {checkoutLoading === 'monthly' && <div className="nav__pricing-spinner"></div>}
                  </button>
                  <button
                    className="nav__pricing-plan nav__pricing-plan--annual"
                    onClick={() => handleCheckout('annual')}
                    disabled={checkoutLoading}
                  >
                    <span className="nav__pricing-plan-badge">{pricing.annual.badge}</span>
                    <span className="nav__pricing-plan-price">${pricing.annual.amount} {pricing.currency}</span>
                    <span className="nav__pricing-plan-period">{pricing.annual.period}</span>
                    {checkoutLoading === 'annual' && <div className="nav__pricing-spinner"></div>}
                  </button>
                  <button
                    className="nav__pricing-plan nav__pricing-plan--lifetime"
                    onClick={() => handleCheckout('lifetime')}
                    disabled={checkoutLoading}
                  >
                    <span className="nav__pricing-plan-badge">{pricing.lifetime.badge}</span>
                    <span className="nav__pricing-plan-price">${pricing.lifetime.amount} {pricing.currency}</span>
                    <span className="nav__pricing-plan-period">{pricing.lifetime.period}</span>
                    {checkoutLoading === 'lifetime' && <div className="nav__pricing-spinner"></div>}
                  </button>
                </div>
              </div>
            )}

            {showPricingMenu && isPremium && (
              <div className="nav__pricing-dropdown">
                <div className="nav__pricing-header">
                  <span className="nav__pricing-title">You're on {getPlanDisplayName()} plan</span>
                </div>
              </div>
            )}
          </div>

          {/* Education icon */}
          <div className="nav__education">
            <div
              className="nav__education-icon"
              onClick={(e) => {
                e.stopPropagation();
                setShowUserMenu(false);
                setShowPricingMenu(false);
                setShowLanguageMenu(false);
                setShowEducationMenu(!showEducationMenu);
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
              </svg>
            </div>

            {showEducationMenu && (
              <div className="nav__education-dropdown">
                <div className="nav__education-header">
                  <span className="nav__education-title">Are you a School or University?</span>
                  <span className="nav__education-subtitle">We have a plan for you</span>
                </div>

                <div className="nav__education-divider"></div>

                <div className="nav__education-content">
                  <h3 className="nav__education-product">Readily for Education</h3>
                  <p className="nav__education-description">
                    A private library system designed for educational institutions
                  </p>

                  <ul className="nav__education-features">
                    <li>Custom catalog on-demand</li>
                    <li>Audiobooks and ebooks your teachers and students need</li>
                    <li>Individual accounts for teachers and students</li>
                    <li>Teachers can upload materials for students to read or download</li>
                    <li>Unlimited reading and listening</li>
                    <li>Annual institutional license</li>
                  </ul>
                </div>

                <div className="nav__education-divider"></div>

                <div className="nav__education-footer">
                  <span className="nav__education-price">Custom pricing for your institution</span>
                  <div className="nav__education-email-row">
                    <span className="nav__education-email">contact@buks.io</span>
                    <button
                      className="nav__education-copy-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText('contact@buks.io');
                        const btn = e.currentTarget;
                        btn.classList.add('copied');
                        setTimeout(() => btn.classList.remove('copied'), 2000);
                      }}
                      title="Copy email"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* User Avatar with Dropdown */}
          <div className="nav__user">
            <div
              className="nav__avatar"
              onClick={(e) => {
                e.stopPropagation();
                setShowPricingMenu(false);
                setShowEducationMenu(false);
                setShowLanguageMenu(false);
                if (user) {
                  setShowUserMenu(!showUserMenu);
                } else {
                  onAvatarClick();
                }
              }}
            >
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Profile"
                  className="nav__avatar-image"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <svg className="nav__avatar-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                </svg>
              )}
            </div>

            {showUserMenu && user && (
              <div className="nav__user-dropdown">
                <div className="nav__user-info">
                  <span className="nav__user-name">{user.displayName || 'User'}</span>
                  <span className="nav__user-email">{user.email}</span>
                </div>
                <div className="nav__user-divider"></div>
                <button className="nav__user-signout" onClick={handleSignOut}>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Hamburger button - mobile only */}
        <div
          className="nav__hamburger"
          onClick={(e) => {
            e.stopPropagation();
            setShowMobileMenu(!showMobileMenu);
          }}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        {/* Mobile menu dropdown */}
        {showMobileMenu && (
          <div className="nav__mobile-menu">
            {/* Main menu */}
            {mobileSubmenu === null && (
              <>
                {/* Premium option */}
                <div
                  className={`nav__mobile-menu-item ${isPremium ? 'nav__mobile-menu-item--premium' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileSubmenu('premium');
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={isPremium ? { color: '#e6c877' } : {}}>
                    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
                  </svg>
                  <span>{isPremium ? getPlanDisplayName() + ' plan' : 'Premium'}</span>
                  <svg className="nav__mobile-menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </div>

                {/* Education option */}
                <div
                  className="nav__mobile-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileSubmenu('education');
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                  </svg>
                  <span>For Schools</span>
                  <svg className="nav__mobile-menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </div>

                {/* Language option */}
                <div
                  className="nav__mobile-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileSubmenu('language');
                  }}
                >
                  <img src={getCurrentLanguage().flag} alt={getCurrentLanguage().name} style={{ width: '20px', height: '15px', objectFit: 'cover', borderRadius: '2px' }} />
                  <span>Language</span>
                  <svg className="nav__mobile-menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </div>

                {/* User option */}
                <div
                  className="nav__mobile-menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (user) {
                      setMobileSubmenu('account');
                    } else {
                      setShowMobileMenu(false);
                      setMobileSubmenu(null);
                      onAvatarClick();
                    }
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                  </svg>
                  <span>{user ? 'Account' : 'Sign In'}</span>
                  {user && (
                    <svg className="nav__mobile-menu-arrow" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                    </svg>
                  )}
                </div>
              </>
            )}

            {/* Premium submenu */}
            {mobileSubmenu === 'premium' && (
              <div className="nav__mobile-submenu">
                <div
                  className="nav__mobile-submenu-back"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileSubmenu(null);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                  </svg>
                  <span>Back</span>
                </div>
                {isPremium ? (
                  <>
                    <div className="nav__mobile-submenu-header">You're on {getPlanDisplayName()} plan</div>
                  </>
                ) : (
                  <>
                    <div className="nav__mobile-submenu-header">Upgrade to Premium</div>
                    <button
                      className="nav__mobile-submenu-plan"
                      onClick={() => {
                        setShowMobileMenu(false);
                        setMobileSubmenu(null);
                        handleCheckout('monthly');
                      }}
                      disabled={checkoutLoading}
                    >
                      <span className="nav__mobile-submenu-plan-price">${pricing.monthly.amount} {pricing.currency}</span>
                      <span className="nav__mobile-submenu-plan-period">{pricing.monthly.period}</span>
                    </button>
                    <button
                      className="nav__mobile-submenu-plan nav__mobile-submenu-plan--annual"
                      onClick={() => {
                        setShowMobileMenu(false);
                        setMobileSubmenu(null);
                        handleCheckout('annual');
                      }}
                      disabled={checkoutLoading}
                    >
                      <span className="nav__mobile-submenu-plan-badge">{pricing.annual.badge}</span>
                      <span className="nav__mobile-submenu-plan-price">${pricing.annual.amount} {pricing.currency}</span>
                      <span className="nav__mobile-submenu-plan-period">{pricing.annual.period}</span>
                    </button>
                    <button
                      className="nav__mobile-submenu-plan nav__mobile-submenu-plan--lifetime"
                      onClick={() => {
                        setShowMobileMenu(false);
                        setMobileSubmenu(null);
                        handleCheckout('lifetime');
                      }}
                      disabled={checkoutLoading}
                    >
                      <span className="nav__mobile-submenu-plan-badge">{pricing.lifetime.badge}</span>
                      <span className="nav__mobile-submenu-plan-price">${pricing.lifetime.amount} {pricing.currency}</span>
                      <span className="nav__mobile-submenu-plan-period">{pricing.lifetime.period}</span>
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Education submenu */}
            {mobileSubmenu === 'education' && (
              <div className="nav__mobile-submenu">
                <div
                  className="nav__mobile-submenu-back"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileSubmenu(null);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                  </svg>
                  <span>Back</span>
                </div>
                <div className="nav__mobile-submenu-header">For Schools</div>
                <div className="nav__mobile-submenu-content">
                  <p className="nav__mobile-submenu-title">Readily for Education</p>
                  <p className="nav__mobile-submenu-desc">A private library system for educational institutions</p>
                  <ul className="nav__mobile-submenu-features">
                    <li>Custom catalog on-demand</li>
                    <li>Individual accounts for teachers and students</li>
                    <li>Unlimited reading and listening</li>
                  </ul>
                  <p className="nav__mobile-submenu-price">Custom pricing for your institution</p>
                  <div className="nav__mobile-submenu-email-row">
                    <span>contact@buks.io</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText('contact@buks.io');
                        const btn = e.currentTarget;
                        btn.textContent = '✓';
                        setTimeout(() => btn.textContent = 'Copy', 2000);
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Account submenu */}
            {mobileSubmenu === 'account' && user && (
              <div className="nav__mobile-submenu">
                <div
                  className="nav__mobile-submenu-back"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileSubmenu(null);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                  </svg>
                  <span>Back</span>
                </div>
                <div className="nav__mobile-submenu-header">Account</div>
                <div className="nav__mobile-submenu-user">
                  <span className="nav__mobile-submenu-user-name">{user.displayName || 'User'}</span>
                  <span className="nav__mobile-submenu-user-email">{user.email}</span>
                </div>
                <button
                  className="nav__mobile-submenu-signout"
                  onClick={() => {
                    setShowMobileMenu(false);
                    setMobileSubmenu(null);
                    handleSignOut();
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}

            {/* Language submenu */}
            {mobileSubmenu === 'language' && (
              <div className="nav__mobile-submenu">
                <div
                  className="nav__mobile-submenu-back"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileSubmenu(null);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                  </svg>
                  <span>Back</span>
                </div>
                <div className="nav__mobile-submenu-header">Showing books in:</div>
                <div className="nav__mobile-submenu-languages">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      className={`nav__mobile-submenu-language ${language === lang.code ? 'nav__mobile-submenu-language--active' : ''}`}
                      onClick={() => {
                        handleLanguageChange(lang.code);
                        setShowMobileMenu(false);
                      }}
                    >
                      <img src={lang.flag} alt={lang.name} className="nav__mobile-submenu-language-flag" />
                      <span className="nav__mobile-submenu-language-name">{lang.name}</span>
                      {language === lang.code && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Nav;
